'use client';

import { VStack } from '@terra/ui-styled-system';
import { FileSearchIcon } from '@terra/ui-icons';
import { ConfirmationDialog } from '../confirmation-dialog/ConfirmationDialog';
import {
  FilterBar,
  FilterBarEmptyState,
  type FilterConfig,
  type FilterValue,
} from '../../components/filter-bar';
import { Skeleton } from '../../components/skeleton';
import { toasterApi } from '../toast/ToastProvider';
import { SettingsEmptyState } from '../settings/SettingsEmptyState';
import { Button } from '../../components/button';
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useTranslations } from 'next-intl';
import { TestingInstructionApplyModeSchema } from '@terra/shared-schemas-orchestrator';
import { TestingInstructionCard } from './TestingInstructionCard';
import { TestingInstructionFormDialog } from './TestingInstructionFormDialog';
import { resolveSelectableInstructionCategories } from './instruction-categories';
import { toTestingControlsCopyScope } from './testing-controls-copy';
import {
  ALL_FILTER_VALUE,
  TESTING_INSTRUCTION_APPLY_MODE_FILTER_ID,
  TESTING_INSTRUCTION_CATEGORY_FILTER_ID,
  TESTING_INSTRUCTION_PIPELINE_FILTER_ID,
} from './constants';
import type {
  TestingInstructionCardActionsVariant,
  TestingInstructionNote,
  TestingInstructionsTarget,
} from './types';
import { FilterBarListScrollLayout } from '../list-page/FilterBarListScrollLayout';

type TestingInstructionsPanelProps = {
  target: TestingInstructionsTarget;
  canManage: boolean;
  actionsVariant?: TestingInstructionCardActionsVariant;
  constrainListScroll?: boolean;
};

export type TestingInstructionsPanelHandle = {
  openCreateDialog: () => void;
};

export const TestingInstructionsPanel = forwardRef<
  TestingInstructionsPanelHandle,
  TestingInstructionsPanelProps
>(function TestingInstructionsPanel(
  { target, canManage, actionsVariant = 'inline', constrainListScroll },
  ref,
) {
  const tPage = useTranslations('designSystem.testingControls');
  const copyScope = toTestingControlsCopyScope(target.targetKind);
  const tInstructions = useTranslations('designSystem.testingInstructions');
  const { notes, isLoading } = target.useNotes();
  const { deleteAsync, isPending: isDeleting } = target.useDeleteNote();
  const selectableCategories = useMemo(
    () =>
      resolveSelectableInstructionCategories({
        targetKind: target.targetKind,
        isDeepSecurityScanEnabled: target.isDeepSecurityScanEnabled ?? false,
      }),
    [target.isDeepSecurityScanEnabled, target.targetKind],
  );
  const [search, setSearch] = useState('');
  const [applyModeFilter, setApplyModeFilter] =
    useState<string>(ALL_FILTER_VALUE);
  const [pipelineFilter, setPipelineFilter] =
    useState<string>(ALL_FILTER_VALUE);
  const [categoryFilter, setCategoryFilter] =
    useState<string>(ALL_FILTER_VALUE);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingNote, setEditingNote] = useState<TestingInstructionNote | null>(
    null,
  );
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [notePendingDelete, setNotePendingDelete] = useState<{
    id: string;
    title: string;
  } | null>(null);

  const filterConfigs: FilterConfig[] = useMemo(
    () => [
      {
        id: TESTING_INSTRUCTION_APPLY_MODE_FILTER_ID,
        placeholder: tInstructions('filters.applyMode'),
        items: TestingInstructionApplyModeSchema.options.map((mode) => ({
          label: tInstructions(`applyModes.${mode}`),
          value: mode,
        })),
      },
      {
        id: TESTING_INSTRUCTION_PIPELINE_FILTER_ID,
        placeholder: tInstructions('filters.pipeline'),
        items: target.availablePipelines.map((pipeline) => ({
          label: tInstructions(`pipelines.${pipeline}`),
          value: pipeline,
        })),
      },
      {
        id: TESTING_INSTRUCTION_CATEGORY_FILTER_ID,
        placeholder: tPage('categoryFilter'),
        items: selectableCategories.map((category) => ({
          label: tInstructions(`categories.${category}`),
          value: category,
        })),
      },
    ],
    [tInstructions, tPage, target.availablePipelines, selectableCategories],
  );

  const filterBarValue: FilterValue = useMemo(() => {
    const next: FilterValue = [];

    if (applyModeFilter !== ALL_FILTER_VALUE) {
      next.push({
        id: TESTING_INSTRUCTION_APPLY_MODE_FILTER_ID,
        value: [applyModeFilter],
      });
    }

    if (pipelineFilter !== ALL_FILTER_VALUE) {
      next.push({
        id: TESTING_INSTRUCTION_PIPELINE_FILTER_ID,
        value: [pipelineFilter],
      });
    }

    if (categoryFilter !== ALL_FILTER_VALUE) {
      next.push({
        id: TESTING_INSTRUCTION_CATEGORY_FILTER_ID,
        value: [categoryFilter],
      });
    }

    return next;
  }, [applyModeFilter, pipelineFilter, categoryFilter]);

  const handleFilterBarChange = useCallback((next: FilterValue) => {
    const applyModeEntry = next.find(
      (entry) => entry.id === TESTING_INSTRUCTION_APPLY_MODE_FILTER_ID,
    );
    const pipelineEntry = next.find(
      (entry) => entry.id === TESTING_INSTRUCTION_PIPELINE_FILTER_ID,
    );
    const categoryEntry = next.find(
      (entry) => entry.id === TESTING_INSTRUCTION_CATEGORY_FILTER_ID,
    );

    setApplyModeFilter(applyModeEntry?.value[0] ?? ALL_FILTER_VALUE);
    setPipelineFilter(pipelineEntry?.value[0] ?? ALL_FILTER_VALUE);
    setCategoryFilter(categoryEntry?.value[0] ?? ALL_FILTER_VALUE);
  }, []);

  const handleSearchChange = useCallback((searchValue: string) => {
    setSearch(searchValue);
  }, []);

  const filteredNotes = useMemo(() => {
    const needle = search.trim().toLowerCase();

    return notes.filter((note) => {
      if (
        applyModeFilter !== ALL_FILTER_VALUE &&
        note.applyMode !== applyModeFilter
      ) {
        return false;
      }

      if (pipelineFilter !== ALL_FILTER_VALUE) {
        if (
          note.applyMode === 'MANUAL' ||
          note.applyMode === 'AGENT_REQUESTED'
        ) {
          return false;
        }

        const matchesPipeline =
          note.pipelines.length === 0 ||
          note.pipelines.some((pipeline) => pipeline === pipelineFilter);

        if (!matchesPipeline) {
          return false;
        }
      }

      if (
        categoryFilter !== ALL_FILTER_VALUE &&
        note.category !== categoryFilter
      ) {
        return false;
      }

      if (!needle) return true;

      return note.title.toLowerCase().includes(needle);
    });
  }, [notes, search, applyModeFilter, pipelineFilter, categoryFilter]);

  const hasNoNotes = notes.length === 0 && !isLoading;

  const handleOpenCreate = useCallback(() => {
    setEditingNote(null);
    setDialogOpen(true);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      openCreateDialog: handleOpenCreate,
    }),
    [handleOpenCreate],
  );

  const handleOpenEdit = useCallback((note: TestingInstructionNote) => {
    setEditingNote(note);
    setDialogOpen(true);
  }, []);

  const handleNoteSaved = useCallback(
    (_saved: TestingInstructionNote) => {
      target.invalidateNotes();
      setDialogOpen(false);
      setEditingNote(null);
    },
    [target],
  );

  const handleRequestDelete = useCallback((noteId: string, title: string) => {
    setNotePendingDelete({ id: noteId, title });
    setDeleteConfirmOpen(true);
  }, []);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmOpen(false);
    setNotePendingDelete(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!notePendingDelete) return;

    try {
      await deleteAsync(notePendingDelete.id);
      toasterApi.success({
        title: tInstructions('success.deleted'),
        description: tInstructions('success.deletedDescription'),
      });
      setDeleteConfirmOpen(false);
      setNotePendingDelete(null);
    } catch {
      toasterApi.error({
        title: tInstructions('errors.failedToDelete'),
        description: tInstructions('errors.failedToDelete'),
      });
    }
  }, [deleteAsync, notePendingDelete, tInstructions]);

  const filterBar = (
    <FilterBar
      filters={filterConfigs}
      value={filterBarValue}
      onFilterChange={handleFilterBarChange}
      onSearch={handleSearchChange}
      searchPlaceholder={tPage('searchInstructionsPlaceholder')}
      searchValue={search}
      px={0}
      pb={4}
      pt={4}
      showBottomBorder={false}
    />
  );

  const listContent = isLoading ? (
    <>
      <Skeleton h="122px" />
      <Skeleton h="122px" />
      <Skeleton h="122px" />
      <Skeleton h="122px" />
    </>
  ) : hasNoNotes ? (
    <SettingsEmptyState
      title={tPage('noPrefix', { prefix: tPage('tabs.testingInstructions') })}
      icon={<FileSearchIcon size={56} />}
      action={
        canManage ? (
          <Button size="sm" variant="solid" onClick={handleOpenCreate}>
            {tPage('addPrefix', {
              prefix: tPage('tabs.testingInstructions'),
            })}
          </Button>
        ) : null
      }
    >
      {tPage(`emptyState.${copyScope}.noTestingInstructionsDescription`)}
    </SettingsEmptyState>
  ) : filteredNotes.length === 0 ? (
    <FilterBarEmptyState />
  ) : (
    filteredNotes.map((note) => (
      <TestingInstructionCard
        key={note.id}
        note={note}
        canManage={canManage}
        actionsVariant={actionsVariant}
        onEdit={() => {
          handleOpenEdit(note);
        }}
        onDelete={() => {
          handleRequestDelete(note.id, note.title);
        }}
      />
    ))
  );

  const dialogs = (
    <>
      <TestingInstructionFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        target={target}
        note={editingNote}
        onSaved={handleNoteSaved}
      />
      <ConfirmationDialog
        isOpen={deleteConfirmOpen}
        loading={isDeleting}
        onOpenChange={({ open }) => {
          if (!open) handleCancelDelete();
        }}
        onConfirm={() => {
          void handleConfirmDelete();
        }}
        onCancel={handleCancelDelete}
        variant="destructive"
        title={tInstructions('delete.title')}
        description={tInstructions('delete.confirmation', {
          title: notePendingDelete?.title ?? '',
        })}
        confirmText={tInstructions('buttons.deleteInstruction')}
      />
    </>
  );

  if (constrainListScroll) {
    return (
      <>
        <FilterBarListScrollLayout filterBar={filterBar}>
          {listContent}
        </FilterBarListScrollLayout>
        {dialogs}
      </>
    );
  }

  return (
    <VStack alignItems="stretch" gap={4} w="full">
      {filterBar}
      {isLoading || hasNoNotes || filteredNotes.length === 0 ? (
        listContent
      ) : (
        <VStack alignItems="stretch" gap={4} w="full">
          {listContent}
        </VStack>
      )}
      {dialogs}
    </VStack>
  );
});
